import React from "react";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { DesktopKpiStrip } from "../shared/DesktopKpiStrip";
import {
  DeskEmpty,
  DeskRowMenu,
  DeskSearch,
  DeskTable,
  DeskTabs,
  DeskToolbar,
} from "../desktop/DeskTable";
import { DueChip, DurationChip, initialsOf, PriorityChip, ProgressBar, TaskCheck, todayIso } from "./TaskParts";
import { progressValue } from "../../helpers/tasks/taskDuration";
import { dueState, GENERAL_LOCATION, TASK_STATUS, taskMetaLabel } from "../../helpers/tasks/taskModel";

/**
 * Desktop Project Tasks workspace: KPI strip, status tabs + location filter,
 * and a task table grouped by location (task, priority, due date, assignee,
 * actions). Same data, permissions and actions as the phone list.
 */
export function ProjectTasksDesktop({
  title,
  projectName,
  onBack,
  tasks,
  groups,
  counts,
  loading,
  offline,
  hasDeviceCopy,
  error,
  status,
  onStatus,
  search,
  onSearch,
  locations,
  scopeLocation,
  locationFilter,
  onLocationFilter,
  featureOptions = [],
  featureFilter = "",
  onFeatureFilter = () => {},
  showLocationFilter,
  showGroupHeaders,
  canManage,
  canComplete,
  mutable,
  busyId,
  names,
  featureName,
  menuFor,
  onOpen,
  onToggle,
  onNew,
  onAshley,
}) {
  const today = todayIso();
  const overdue = tasks.filter((t) => t.status !== TASK_STATUS.COMPLETED && dueState(t, today) === "overdue").length;

  const columns = [
    {
      key: "check",
      label: "",
      width: "52px",
      align: "center",
      className: "dt-cell--control",
      render: (task) => (
        <TaskCheck
          task={task}
          interactive={canComplete && mutable}
          busy={busyId === task.project_task_id}
          onToggle={onToggle}
        />
      ),
    },
    {
      key: "title",
      label: "Task",
      width: "30%",
      primary: true,
      render: (task) => {
        const done = task.status === TASK_STATUS.COMPLETED;
        return (
          <>
            <span className={`dt-strong${done ? " tk-done-title" : ""}`}>{task.title}</span>
            {task.feature_id ? (
              <span className="dt-sub">{featureName[task.feature_id] || "Feature"}</span>
            ) : done ? (
              <span className="dt-sub">{taskMetaLabel(task)}</span>
            ) : null}
          </>
        );
      },
    },
    {
      key: "priority",
      label: "Priority",
      width: "14%",
      render: (task) => (task.priority ? <PriorityChip priority={task.priority} /> : <span className="dt-muted">—</span>),
    },
    {
      key: "due",
      label: "Due",
      width: "17%",
      render: (task) =>
        task.status === TASK_STATUS.COMPLETED || !task.due_date ? (
          <span className="dt-muted">{task.status === TASK_STATUS.COMPLETED ? "Done" : "No due date"}</span>
        ) : (
          <DueChip task={task} today={today} />
        ),
    },
    {
      key: "progress",
      label: "Progress",
      width: "12%",
      render: (task) => {
        const value = progressValue(task);
        return (
          <span className="tk-progress-cell">
            {value == null ? <span className="dt-muted">—</span> : (
              <>
                <ProgressBar value={value} />
                <span className="dt-muted dt-nowrap">{value}%</span>
              </>
            )}
            <DurationChip minutes={task.planned_duration_minutes} />
          </span>
        );
      },
    },
    {
      key: "assignee",
      label: "Assignee",
      width: "17%",
      render: (task) =>
        names[task.assignee_id] ? (
          <span className="tk-assignee">
            <span className="tk-avatar" aria-hidden="true">{initialsOf(names[task.assignee_id])}</span>
            <span className="dt-strong">{names[task.assignee_id]}</span>
          </span>
        ) : (
          <span className="dt-muted">Unassigned</span>
        ),
    },
    {
      key: "actions",
      label: "",
      className: "dt-cell--actions",
      width: "56px",
      render: (task) => (
        <DeskRowMenu label={`Actions for ${task.title}`} items={menuFor(task)} onSelect={(item) => item.onSelect(task)} />
      ),
    },
  ];

  const sections = groups.map((group) => ({
    key: group.locationId || "general",
    header: showGroupHeaders ? (
      <span className="dt-group__row">
        <span>{group.label}</span>
        <span className="dt-group__total">
          {group.total} {group.total === 1 ? "task" : "tasks"} · {group.open} open
        </span>
      </span>
    ) : null,
    rows: group.tasks,
  }));

  return (
    <CrewPilotModuleShell
      title={title}
      subtitle={projectName || ""}
      onBack={onBack}
      desktopBackLabel={projectName || "Project"}
      crumbs={["Projects", projectName]}
      layout={PAGE_LAYOUT.COMMAND}
      compact
      headerActions={
        <div className="cdh-actions">
          <DeskSearch value={search} onChange={onSearch} placeholder="Search tasks…" ariaLabel="Search tasks" />
          {canManage && !offline ? (
            <>
              <button type="button" className="dt-btn" aria-label="Build Tasks with Ashley" onClick={onAshley}>
                ✨ Build with Ashley
              </button>
              <button type="button" className="dt-btn dt-btn--primary" aria-label="+ New Task" onClick={onNew}>
                + New Task
              </button>
            </>
          ) : null}
        </div>
      }
    >
      <div data-testid="project-tasks">
        {offline ? (
          <div className="tk-notice" role="status" data-testid="tasks-offline-notice">
            {hasDeviceCopy
              ? "Showing tasks saved on this device. Reconnect to change them."
              : "Tasks for this project are not saved on this device yet. Open them once while online."}
          </div>
        ) : null}
        {error ? (
          <div className="rec-banner" role="alert" style={{ marginBottom: 12 }}>
            {error}
          </div>
        ) : null}

        <DesktopKpiStrip
          ariaLabel="Task summary"
          tiles={[
            { key: "total", value: String(counts.total), label: "Tasks", tone: "gold" },
            { key: "open", value: String(counts.open), label: "Open", tone: "blue" },
            { key: "done", value: String(counts.completed), label: "Completed", tone: "green" },
            { key: "overdue", value: String(overdue), label: "Overdue", tone: overdue ? "red" : "muted" },
          ]}
        />

        <DeskToolbar>
          <DeskTabs
            ariaLabel="Task status"
            value={status}
            onChange={onStatus}
            options={[
              { value: "all", label: "All", count: counts.total },
              { value: TASK_STATUS.OPEN, label: "Open", count: counts.open },
              { value: TASK_STATUS.COMPLETED, label: "Completed", count: counts.completed },
            ]}
          />
          {showLocationFilter && !scopeLocation ? (
            <label className="dt-select">
              <span>Location</span>
              <select aria-label="Location filter" value={locationFilter} onChange={(e) => onLocationFilter(e.target.value)}>
                <option value="">All Locations</option>
                {locations.map((l) => (
                  <option key={l.project_location_id} value={l.project_location_id}>
                    {l.name}
                  </option>
                ))}
                <option value={GENERAL_LOCATION}>General (whole project)</option>
              </select>
            </label>
          ) : null}
          {featureOptions.length ? (
            <label className="dt-select">
              <span>Feature</span>
              <select aria-label="Feature filter" value={featureFilter} onChange={(e) => onFeatureFilter(e.target.value)}>
                <option value="">All Features</option>
                {featureOptions.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </DeskToolbar>

        {loading ? (
          <DeskEmpty title="Loading tasks…" />
        ) : sections.length === 0 ? (
          <div data-testid="tasks-empty">
            <DeskEmpty title={tasks.length === 0 ? "No tasks yet" : "No tasks match these filters"}>
              {tasks.length === 0 && canManage && !offline ? "Add the first one with New Task." : ""}
            </DeskEmpty>
          </div>
        ) : (
          <DeskTable
            testId="project-tasks-table"
            caption="Project tasks"
            columns={columns}
            groups={sections}
            rowKey={(task) => task.project_task_id}
            onOpen={onOpen}
            openLabel={(task) => `Open task ${task.title}`}
            rowClassName={(task) => (task.status === TASK_STATUS.COMPLETED ? "is-done" : "")}
          />
        )}
      </div>
    </CrewPilotModuleShell>
  );
}
